import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Switch,
  TextField,
  Typography,
  useTheme,
  alpha,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  FormGroup,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Tooltip,
  Link
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  DragIndicator as DragIndicatorIcon,
  Timeline as TimelineIcon,
  ShortText as ShortTextIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  Email as EmailIcon,
  CheckBox as CheckBoxIcon,
  RadioButtonChecked as RadioIcon,
  DateRange as DateIcon,
  MenuOpen as SelectIcon,
  ContentCopy as DuplicateIcon,
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  PlaylistAdd as PlaylistAddIcon,
  Info as InfoIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import workflowService from '../../../services/workflowService';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Field' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'date', label: 'Date Picker' },
  { value: 'number', label: 'Number Field' },
];

const ACTION_TYPES = [
  { value: 'approve', label: 'Approve' },
  { value: 'reject', label: 'Reject' },
  { value: 'request_info', label: 'Request Information' },
];

const EditWorkflowTemplatePage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const theme = useTheme(); // Make sure theme is defined using useTheme hook

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState([]);
  const [formFields, setFormFields] = useState([]);

  const [showStepDialog, setShowStepDialog] = useState(false);
  const [stepFormData, setStepFormData] = useState({
    name: '',
    description: '',
    slaHours: '24',
    assignedRoles: [],
    actions: [],
  });
  const [currentEditingStep, setCurrentEditingStep] = useState(null);

  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [fieldFormData, setFieldFormData] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false,
    helpText: '',
    placeholder: '',
    defaultValue: '',
    options: [],
  });
  const [currentEditingField, setCurrentEditingField] = useState(null);

  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionFormData, setActionFormData] = useState({
    id: '',
    label: '',
    type: 'approve',
    description: '',
    performer: 'admin',
    nextStepId: '',
    resultStatus: '',
  });

  const [showOptionDialog, setShowOptionDialog] = useState(false);
  const [currentOption, setCurrentOption] = useState({ label: '', value: '' });
  const [currentOptionIndex, setCurrentOptionIndex] = useState(-1);

  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const editingSteps = ['Basic Information', 'Define Steps', 'Create Form Fields', 'Review & Save'];

  const isStaffPortal = window.location.pathname.includes('/staff');

  useEffect(() => {
    const fetchTemplateData = async () => {
      setLoading(true);
      try {
        const response = await workflowService.getWorkflowTemplateById(templateId);
        if (!response.data) {
          throw new Error('Template not found');
        }

        setTemplateName(response.data.name || '');
        setTemplateDescription(response.data.description || '');
        setIsActive(response.data.isActive || false);

        if (response.data.steps && Array.isArray(response.data.steps)) {
          const stepsWithOrder = response.data.steps.map((step, index) => ({
            ...step,
            order: step.order !== undefined ? step.order : index,
          }));
          setSteps(stepsWithOrder);
        }

        if (response.data.formFields && Array.isArray(response.data.formFields)) {
          setFormFields(response.data.formFields);
        }

        setInitialLoad(false);
      } catch (err) {
        console.error('Error fetching workflow template:', err);
        setError(err.message || 'Error loading workflow template. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplateData();
  }, [templateId]);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCancel = () => {
    // Redirect back to templates page based on user role context
    if (isStaffPortal) {
      navigate('/staff/workflows/templates');
    } else {
      navigate('/admin/workflows/templates');
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!templateName.trim()) {
          newErrors.templateName = 'Template name is required';
        }
        break;
      case 1:
        if (steps.length === 0) {
          newErrors.steps = 'At least one step is required';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddStep = () => {
    setCurrentEditingStep(null);
    setStepFormData({
      name: '',
      description: '',
      slaHours: '24',
      assignedRoles: [],
      actions: [],
    });
    setShowStepDialog(true);
  };

  const handleEditStep = (index) => {
    setCurrentEditingStep(index);
    const step = steps[index];
    setStepFormData({
      name: step.name || '',
      description: step.description || '',
      slaHours: step.slaHours ? String(step.slaHours) : '24',
      assignedRoles: step.assignedRoles || [],
      actions: step.actions || [],
    });
    setShowStepDialog(true);
  };

  const handleDeleteStep = (index) => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);

    const updatedSteps = newSteps.map((step, idx) => ({
      ...step,
      order: idx,
    }));

    setSteps(updatedSteps);
  };

  const handleSaveStep = () => {
    if (!stepFormData.name.trim()) {
      setErrors({
        ...errors,
        stepName: 'Step name is required',
      });
      return;
    }

    const newSteps = [...steps];
    const stepData = {
      ...stepFormData,
      slaHours: parseInt(stepFormData.slaHours, 10) || 24,
    };

    if (currentEditingStep !== null) {
      newSteps[currentEditingStep] = {
        ...newSteps[currentEditingStep],
        ...stepData,
      };
    } else {
      newSteps.push({
        ...stepData,
        order: newSteps.length,
      });
    }

    setSteps(newSteps);
    setShowStepDialog(false);
    setErrors({
      ...errors,
      stepName: undefined,
      steps: undefined,
    });
  };

  const handleMoveStepUp = (index) => {
    if (index > 0) {
      const newSteps = [...steps];
      [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];

      const updatedSteps = newSteps.map((step, idx) => ({
        ...step,
        order: idx,
      }));

      setSteps(updatedSteps);
    }
  };

  const handleMoveStepDown = (index) => {
    if (index < steps.length - 1) {
      const newSteps = [...steps];
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];

      const updatedSteps = newSteps.map((step, idx) => ({
        ...step,
        order: idx,
      }));

      setSteps(updatedSteps);
    }
  };

  const handleStepDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((step, idx) => ({
      ...step,
      order: idx,
    }));

    setSteps(updatedItems);
  };

  const handleAddAction = () => {
    setActionFormData({
      id: `action_${Date.now()}`,
      label: '',
      type: 'approve',
      description: '',
      performer: 'admin',
      nextStepId: '',
      resultStatus: '',
    });
    setShowActionDialog(true);
  };

  const handleEditAction = (actionIndex) => {
    const action = stepFormData.actions[actionIndex];
    setActionFormData({ ...action });
    setShowActionDialog(true);
  };

  const handleDeleteAction = (actionIndex) => {
    const newActions = [...stepFormData.actions];
    newActions.splice(actionIndex, 1);
    setStepFormData({
      ...stepFormData,
      actions: newActions,
    });
  };

  const handleSaveAction = () => {
    if (!actionFormData.label.trim()) {
      setErrors({
        ...errors,
        actionLabel: 'Action label is required',
      });
      return;
    }

    const newActions = [...stepFormData.actions];
    const existingActionIndex = newActions.findIndex((action) => action.id === actionFormData.id);

    if (existingActionIndex !== -1) {
      newActions[existingActionIndex] = actionFormData;
    } else {
      newActions.push(actionFormData);
    }

    setStepFormData({
      ...stepFormData,
      actions: newActions,
    });

    setShowActionDialog(false);
    setErrors({
      ...errors,
      actionLabel: undefined,
    });
  };

  const handleAddFormField = () => {
    setCurrentEditingField(null);
    setFieldFormData({
      name: '',
      label: '',
      type: 'text',
      required: false,
      helpText: '',
      placeholder: '',
      defaultValue: '',
      options: [],
    });
    setShowFieldDialog(true);
  };

  const handleEditFormField = (index) => {
    setCurrentEditingField(index);
    const field = formFields[index];
    setFieldFormData({
      ...field,
    });
    setShowFieldDialog(true);
  };

  const handleDeleteFormField = (index) => {
    const newFormFields = [...formFields];
    newFormFields.splice(index, 1);
    setFormFields(newFormFields);
  };

  const handleSaveFormField = () => {
    if (!fieldFormData.label.trim()) {
      setErrors({
        ...errors,
        fieldLabel: 'Field label is required',
      });
      return;
    }

    if (!fieldFormData.name.trim()) {
      const generatedName = fieldFormData.label
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

      fieldFormData.name = generatedName;
    }

    if (
      ['select', 'radio', 'checkbox'].includes(fieldFormData.type) &&
      (!fieldFormData.options || fieldFormData.options.length === 0)
    ) {
      setErrors({
        ...errors,
        fieldOptions: 'At least one option is required for this field type',
      });
      return;
    }

    const newFormFields = [...formFields];

    if (currentEditingField !== null) {
      newFormFields[currentEditingField] = fieldFormData;
    } else {
      newFormFields.push(fieldFormData);
    }

    setFormFields(newFormFields);
    setShowFieldDialog(false);
    setErrors({
      ...errors,
      fieldLabel: undefined,
      fieldOptions: undefined,
    });
  };

  const handleAddOption = () => {
    setCurrentOption({ label: '', value: '' });
    setCurrentOptionIndex(-1);
    setShowOptionDialog(true);
  };

  const handleEditOption = (index) => {
    setCurrentOption({ ...fieldFormData.options[index] });
    setCurrentOptionIndex(index);
    setShowOptionDialog(true);
  };

  const handleDeleteOption = (index) => {
    const newOptions = [...fieldFormData.options];
    newOptions.splice(index, 1);
    setFieldFormData({
      ...fieldFormData,
      options: newOptions,
    });
  };

  const handleSaveOption = () => {
    if (!currentOption.label.trim()) {
      setErrors({
        ...errors,
        optionLabel: 'Option label is required',
      });
      return;
    }

    if (!currentOption.value.trim()) {
      currentOption.value = currentOption.label;
    }

    const newOptions = [...fieldFormData.options];

    if (currentOptionIndex !== -1) {
      newOptions[currentOptionIndex] = currentOption;
    } else {
      newOptions.push(currentOption);
    }

    setFieldFormData({
      ...fieldFormData,
      options: newOptions,
    });

    setShowOptionDialog(false);
    setErrors({
      ...errors,
      optionLabel: undefined,
    });
  };

  const handleUpdateTemplate = async () => {
    for (let i = 0; i < editingSteps.length; i++) {
      if (!validateStep(i)) {
        setActiveStep(i);
        return;
      }
    }

    setSaving(true);
    try {
      const templateData = {
        name: templateName,
        description: templateDescription,
        isActive,
        steps,
        formFields,
      };

      // Use the correct method name from workflowService
      await workflowService.updateWorkflowTemplate(templateId, templateData);

      setSuccess(true);

      setTimeout(() => {
        navigate('/admin/workflows/templates');
      }, 2000); // Fix: added missing closing brace and parenthesis for setTimeout
    } catch (err) {
      console.error('Error updating workflow template:', err);
      setError(err.message || 'An error occurred while saving the template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Basic Template Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <TextField
              label="Template Name"
              fullWidth
              required
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              error={!!errors.templateName}
              helperText={errors.templateName || 'Enter a descriptive name for this workflow template'}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              helperText="Provide details about this workflow template's purpose and usage"
              sx={{ mb: 3 }}
            />

            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
              label="Make this template active and available for members to use"
              sx={{ mb: 2 }}
            />

            <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <InfoIcon color="info" sx={{ mr: 1, mt: 0.5 }} />
                <Typography variant="body2">
                  The workflow template defines the process and steps for approval workflows. Members will be able to
                  create requests based on this template, and these requests will follow the steps defined here.
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Define Workflow Steps</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddStep} sx={{ borderRadius: 1 }}>
                Add Step
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {errors.steps && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.steps}
              </Alert>
            )}

            {steps.length === 0 ? (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  borderWidth: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <PlaylistAddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Steps Defined
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Add steps to define the workflow process from start to finish.
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddStep} sx={{ mt: 1 }}>
                  Add First Step
                </Button>
              </Paper>
            ) : (
              <DragDropContext onDragEnd={handleStepDragEnd}>
                <Droppable droppableId="workflow-steps">
                  {(provided) => (
                    <List
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {steps.map((step, index) => (
                        <Draggable key={index} draggableId={`step-${index}`} index={index}>
                          {(provided) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              divider={index < steps.length - 1}
                              sx={{
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' },
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Box {...provided.dragHandleProps} sx={{ mr: 1, display: 'flex', color: 'text.secondary' }}>
                                <DragIndicatorIcon />
                              </Box>

                              <Box sx={{ mr: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveStepUp(index)}
                                  disabled={index === 0}
                                  sx={{ mb: 0.5 }}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveStepDown(index)}
                                  disabled={index === steps.length - 1}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    {step.name}
                                  </Typography>
                                  <Chip size="small" label={`SLA: ${step.slaHours}h`} color="primary" variant="outlined" />
                                </Box>

                                {step.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {step.description}
                                  </Typography>
                                )}

                                {step.actions && step.actions.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', mb: 0.5 }}
                                    >
                                      Actions:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {step.actions.map((action, i) => (
                                        <Chip
                                          key={i}
                                          size="small"
                                          label={action.label}
                                          sx={{
                                            bgcolor: (() => {
                                              switch (action.type) {
                                                case 'approve':
                                                  return alpha(theme.palette.success.main, 0.1);
                                                case 'reject':
                                                  return alpha(theme.palette.error.main, 0.1);
                                                case 'request_info':
                                                  return alpha(theme.palette.info.main, 0.1);
                                                default:
                                                  return alpha(theme.palette.grey[500], 0.1);
                                              }
                                            })(),
                                            color: (() => {
                                              switch (action.type) {
                                                case 'approve':
                                                  return theme.palette.success.main;
                                                case 'reject':
                                                  return theme.palette.error.main;
                                                case 'request_info':
                                                  return theme.palette.info.main;
                                                default:
                                                  return theme.palette.text.primary;
                                              }
                                            })(),
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>

                              <Box sx={{ display: 'flex', ml: 2 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditStep(index)}
                                  color="primary"
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteStep(index)} color="error">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </ListItem>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            <Paper sx={{ p: 2, mt: 3, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <InfoIcon color="info" sx={{ mr: 1, mt: 0.5 }} />
                <Typography variant="body2">
                  Define the steps that a workflow request will go through. Each step can have its own actions, such as
                  approve, reject, or request more information. You can also set SLA (Service Level Agreement) time for
                  each step.
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Define Form Fields</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFormField} sx={{ borderRadius: 1 }}>
                Add Field
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {formFields.length === 0 ? (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  borderWidth: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Form Fields Defined
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Add form fields to collect information from members when they create a workflow request.
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddFormField} sx={{ mt: 1 }}>
                  Add First Field
                </Button>
              </Paper>
            ) : (
              <List
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                {formFields.map((field, index) => (
                  <ListItem
                    key={index}
                    divider={index < formFields.length - 1}
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {field.label}
                          </Typography>
                          {field.required && (
                            <Chip
                              size="small"
                              label="Required"
                              color="error"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Chip
                              size="small"
                              label={FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Field Name: {field.name}
                            </Typography>
                          </Box>

                          {field.helpText && (
                            <Typography variant="body2" color="text.secondary">
                              Help Text: {field.helpText}
                            </Typography>
                          )}

                          {['select', 'radio', 'checkbox'].includes(field.type) && field.options?.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Options: {field.options.map((o) => o.label).join(', ')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />

                    <Box sx={{ display: 'flex' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditFormField(index)}
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteFormField(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}

            <Paper sx={{ p: 2, mt: 3, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <InfoIcon color="info" sx={{ mr: 1, mt: 0.5 }} />
                <Typography variant="body2">
                  Define the form fields that will be displayed to members when they create a workflow request. You can
                  create various field types such as text fields, dropdowns, checkboxes, and more. Form fields are
                  optional - if you don't create any, only the basic request information will be collected.
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Template
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ mb: 3, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Basic Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Template Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {templateName}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={isActive ? 'Active' : 'Draft'}
                        color={isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    {templateDescription && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Description
                        </Typography>
                        <Typography variant="body2">{templateDescription}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ mb: 3, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Workflow Process
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {steps.length > 0 ? (
                      <Stepper orientation="vertical" sx={{ mt: 2 }}>
                        {steps.map((step, index) => (
                          <Step key={index} active={true}>
                            <StepLabel>
                              <Typography variant="subtitle2">{step.name}</Typography>
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No steps defined
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Form Fields
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {formFields.length > 0 ? (
                      <Grid container spacing={2}>
                        {formFields.map((field, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                height: '100%',
                              }}
                            >
                              <Box sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                    {field.label}
                                  </Typography>
                                  {field.required && (
                                    <Typography variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                                      *
                                    </Typography>
                                  )}
                                </Box>
                                <Chip
                                  size="small"
                                  label={FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                                  sx={{ mb: 1 }}
                                />
                              </Box>

                              {field.helpText && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {field.helpText}
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No form fields defined
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper sx={{ p: 2, mt: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <InfoIcon color="warning" sx={{ mr: 1, mt: 0.5 }} />
                <Typography variant="body2">
                  Please review all template information carefully before saving. After updating, these changes will
                  affect new workflow requests based on this template.
                </Typography>
              </Box>
            </Paper>
          </Box>
        );

      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  if (loading && initialLoad) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh',
            flexDirection: 'column',
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 3 }}>
            Loading template data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ backgroundColor: alpha(theme.palette.primary.light, 0.04), minHeight: '100vh' }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconButton
              color="inherit"
              onClick={handleCancel}
              sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h5"
                component="h1"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <TimelineIcon sx={{ mr: 1.5 }} />
                Edit Workflow Template
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, mt: 0.5 }}>
                Modify an existing workflow process
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/admin/workflows"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Workflows
          </Link>
          <Link
            color="inherit"
            href="/admin/workflows/templates"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="small" />
            Templates
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon sx={{ mr: 0.5 }} fontSize="small" />
            Edit Template
          </Typography>
        </Breadcrumbs>

        <Card
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
              {editingSteps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        ...(index === activeStep && {
                          '& .MuiStepIcon-root': { color: 'primary.main' },
                        }),
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {getStepContent(activeStep)}

            {error && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: 1 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                onClick={activeStep === 0 ? handleCancel : handleBack}
                disabled={loading || saving}
                sx={{ borderRadius: 1 }}
                startIcon={activeStep === 0 ? <ArrowBackIcon /> : undefined}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </Button>

              <Box>
                {activeStep === editingSteps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateTemplate}
                    disabled={loading || saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    sx={{
                      borderRadius: 1,
                      px: 3,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    {saving ? 'Saving...' : 'Update Template'}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleNext} sx={{ borderRadius: 1 }}>
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Dialog open={showStepDialog} onClose={() => setShowStepDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{currentEditingStep !== null ? 'Edit Workflow Step' : 'Add Workflow Step'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Step Name"
                  fullWidth
                  required
                  value={stepFormData.name}
                  onChange={(e) => setStepFormData({ ...stepFormData, name: e.target.value })}
                  error={!!errors.stepName}
                  helperText={errors.stepName || 'Enter a descriptive name for this step'}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="SLA Hours"
                  fullWidth
                  type="number"
                  value={stepFormData.slaHours}
                  onChange={(e) => setStepFormData({ ...stepFormData, slaHours: e.target.value })}
                  helperText="Time limit for this step"
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Step Description"
                  fullWidth
                  multiline
                  rows={2}
                  value={stepFormData.description}
                  onChange={(e) => setStepFormData({ ...stepFormData, description: e.target.value })}
                  helperText="Optional description for this step"
                  sx={{ mb: 3 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Step Actions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Define actions that can be performed at this step of the workflow
                </Typography>

                {stepFormData.actions.length > 0 ? (
                  <List
                    sx={{
                      mb: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    {stepFormData.actions.map((action, index) => (
                      <ListItem
                        key={index}
                        divider={index < stepFormData.actions.length - 1}
                        secondaryAction={
                          <Box>
                            <IconButton edge="end" aria-label="edit" onClick={() => handleEditAction(index)} sx={{ mr: 1 }}>
                              <EditIcon />
                            </IconButton>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAction(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={action.label}
                          secondary={
                            <Box>
                              <Typography variant="body2" component="span">
                                Type: {action.type} • Performer: {action.performer}
                              </Typography>
                              {action.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {action.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No actions defined for this step
                    </Typography>
                  </Box>
                )}

                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddAction} fullWidth>
                  Add Action
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowStepDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveStep}
              variant="contained"
              color="primary"
              disabled={!stepFormData.name.trim()}
            >
              Save Step
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showActionDialog} onClose={() => setShowActionDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{actionFormData.id ? 'Edit Action' : 'Add Action'}</DialogTitle>
          <DialogContent dividers>
            <TextField
              label="Action Label"
              fullWidth
              required
              value={actionFormData.label}
              onChange={(e) => setActionFormData({ ...actionFormData, label: e.target.value })}
              error={!!errors.actionLabel}
              helperText={errors.actionLabel || 'Enter a label for this action (e.g., Approve, Reject)'}
              sx={{ mb: 2, mt: 1 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="action-type-label">Action Type</InputLabel>
              <Select
                labelId="action-type-label"
                value={actionFormData.type}
                onChange={(e) => setActionFormData({ ...actionFormData, type: e.target.value })}
                label="Action Type"
              >
                {ACTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select the type of action this represents</FormHelperText>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="performer-label">Performer</InputLabel>
              <Select
                labelId="performer-label"
                value={actionFormData.performer}
                onChange={(e) => setActionFormData({ ...actionFormData, performer: e.target.value })}
                label="Performer"
              >
                <MenuItem value="admin">Admin Only</MenuItem>
                <MenuItem value="member">Member Only</MenuItem>
                <MenuItem value="both">Both Admin & Member</MenuItem>
              </Select>
              <FormHelperText>Who can perform this action</FormHelperText>
            </FormControl>

            <TextField
              label="Action Description"
              fullWidth
              multiline
              rows={2}
              value={actionFormData.description}
              onChange={(e) => setActionFormData({ ...actionFormData, description: e.target.value })}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="next-step-label">Next Step (Optional)</InputLabel>
              <Select
                labelId="next-step-label"
                value={actionFormData.nextStepId}
                onChange={(e) => setActionFormData({ ...actionFormData, nextStepId: e.target.value })}
                label="Next Step (Optional)"
              >
                <MenuItem value="">
                  <em>Default progression</em>
                </MenuItem>
                {steps.map((step, index) => (
                  <MenuItem key={index} value={index.toString()}>
                    {step.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Which step should follow after this action is taken (optional)
              </FormHelperText>
            </FormControl>

            <TextField
              label="Result Status (Optional)"
              fullWidth
              value={actionFormData.resultStatus}
              onChange={(e) => setActionFormData({ ...actionFormData, resultStatus: e.target.value })}
              helperText="If set, will change the request status after this action (e.g., Approved, Declined)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowActionDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveAction}
              variant="contained"
              color="primary"
              disabled={!actionFormData.label.trim()}
            >
              Save Action
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showFieldDialog} onClose={() => setShowFieldDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{currentEditingField !== null ? 'Edit Form Field' : 'Add Form Field'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Field Label"
                  fullWidth
                  required
                  value={fieldFormData.label}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })}
                  error={!!errors.fieldLabel}
                  helperText={errors.fieldLabel || 'Label shown to users'}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Field Name"
                  fullWidth
                  value={fieldFormData.name}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, name: e.target.value })}
                  helperText="Technical name (will be auto-generated if left blank)"
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="field-type-label">Field Type</InputLabel>
                  <Select
                    labelId="field-type-label"
                    value={fieldFormData.type}
                    onChange={(e) =>
                      setFieldFormData({
                        ...fieldFormData,
                        type: e.target.value,
                        options: ['select', 'radio', 'checkbox'].includes(e.target.value)
                          ? fieldFormData.options
                          : [],
                      })
                    }
                    label="Field Type"
                  >
                    {FIELD_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldFormData.required}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, required: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Required field"
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Help Text"
                  fullWidth
                  value={fieldFormData.helpText}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, helpText: e.target.value })}
                  helperText="Explanatory text shown below the field"
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Placeholder"
                  fullWidth
                  value={fieldFormData.placeholder}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, placeholder: e.target.value })}
                  helperText="Text shown when field is empty"
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Default Value"
                  fullWidth
                  value={fieldFormData.defaultValue}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, defaultValue: e.target.value })}
                  helperText="Pre-filled value"
                  sx={{ mb: 2 }}
                />
              </Grid>

              {['select', 'radio', 'checkbox'].includes(fieldFormData.type) && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Field Options
                    </Typography>
                    {errors.fieldOptions && <FormHelperText error>{errors.fieldOptions}</FormHelperText>}

                    {fieldFormData.options.length > 0 ? (
                      <List
                        sx={{
                          mb: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        {fieldFormData.options.map((option, index) => (
                          <ListItem
                            key={index}
                            divider={index < fieldFormData.options.length - 1}
                            secondaryAction={
                              <Box>
                                <IconButton
                                  edge="end"
                                  aria-label="edit"
                                  onClick={() => handleEditOption(index)}
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteOption(index)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            }
                          >
                            <ListItemText
                              primary={option.label}
                              secondary={option.value !== option.label ? `Value: ${option.value}` : null}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No options defined
                        </Typography>
                      </Box>
                    )}

                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddOption} fullWidth>
                      Add Option
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowFieldDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveFormField}
              variant="contained"
              color="primary"
              disabled={!fieldFormData.label.trim()}
            >
              Save Field
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showOptionDialog} onClose={() => setShowOptionDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{currentOptionIndex !== -1 ? 'Edit Option' : 'Add Option'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Option Label"
              fullWidth
              required
              value={currentOption.label}
              onChange={(e) => setCurrentOption({ ...currentOption, label: e.target.value })}
              error={!!errors.optionLabel}
              helperText={errors.optionLabel || 'Text displayed to users'}
              sx={{ mb: 2, mt: 1 }}
            />

            <TextField
              label="Option Value"
              fullWidth
              value={currentOption.value}
              onChange={(e) => setCurrentOption({ ...currentOption, value: e.target.value })}
              helperText="Internal value (leave blank to use label)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowOptionDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveOption}
              variant="contained"
              color="primary"
              disabled={!currentOption.label.trim()}
            >
              Save Option
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSuccess(false)}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
            icon={<CheckCircleIcon />}
          >
            Workflow template updated successfully!
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default EditWorkflowTemplatePage;